import React, { createContext, useCallback, useEffect, useLayoutEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import i18n from "../i18n";
import { handleUnauthorized } from "../pages/util/helper";
import { clearUnauthorizedHandler, clientFetch, initCsrfToken, setUnauthorizedHandler } from "../utils/clientFetch";

export const AuthContext = createContext<{
  isAuthenticated: boolean | undefined
}>({
  isAuthenticated: undefined
})

export const UpdateAuthContext = createContext<{
  login: (value: { email: string, password: string }) => Promise<{ ok: boolean, errorMessage?: string }>
}>({
  login: async (value: { email: string, password: string }) => ({ ok: false })
})

export function useAuthContext() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | undefined>(undefined)
  const HTTP_STATUS = {
    unauthorized: 401,
    forbidden: 403
  }
  const ABORT_ERROR_NAME = "AbortError"
  const CSRF_TOKEN_PATH = "/apis/admin/csrf-token"

  useEffect(() => {
    const abortController = new AbortController()

    // 初回表示時に未認証ユーザーの画面ちらつき（管理画面が一瞬見える）を防ぐための認証確認
    const checkAuth = async () => {
      try {
        const res = await clientFetch(
          `${process.env.REACT_APP_API_ENDPOINT}/apis/admin/session`,
          { signal: abortController.signal }
        )
        setIsAuthenticated(res.ok)
      } catch (error) {
        if (error instanceof Error && error.name === ABORT_ERROR_NAME) {
          return
        }
        console.error("認証状態の確認に失敗しました。", error)
        setIsAuthenticated(false)
      }
    }

    const initialize = async () => {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT
      if (!apiEndpoint) {
        console.error("APIエンドポイントが未設定です。")
        setIsAuthenticated(false)
        return
      }

      try {
        await initCsrfToken(`${apiEndpoint}${CSRF_TOKEN_PATH}`, abortController.signal)
      } catch (error) {
        const errorName = error instanceof Error ? error.name : ""
        if (errorName === ABORT_ERROR_NAME) {
          return
        }
        console.error("CSRFトークン初期化に失敗しました。", error)
      }

      await checkAuth()
    }

    initialize()

    return () => {
      abortController.abort()
    }
  }, [])

  const login = useCallback(async (value: { email: string, password: string }) => {
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT
      if (!apiEndpoint) {
        console.error("APIエンドポイントが未設定です。")
        setIsAuthenticated(false)
        return { ok: false, errorMessage: i18n.t("auth.loginFailed") }
      }

      try {
        await initCsrfToken(`${apiEndpoint}${CSRF_TOKEN_PATH}`)
      } catch (error) {
        console.error("CSRFトークン初期化に失敗しました。", error)
        setIsAuthenticated(false)
        return { ok: false, errorMessage: i18n.t("auth.loginFailed") }
      }

      const res = await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/login`, {
        method: "POST",
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(value)
      })

      if (!res.ok) {
        if (res.status === HTTP_STATUS.unauthorized) {
          setIsAuthenticated(false)
          return { ok: false, errorMessage: i18n.t("auth.invalidCredentials") }
        }
        if (res.status === HTTP_STATUS.forbidden) {
          setIsAuthenticated(false)
          return { ok: false, errorMessage: i18n.t("auth.loginFailed") }
        }
        setIsAuthenticated(false)
        return { ok: false, errorMessage: i18n.t("auth.loginFailed") }
      }

      setIsAuthenticated(true)
      return { ok: true }
    } catch {
      console.error('ログイン失敗')
      setIsAuthenticated(false)
      return { ok: false, errorMessage: i18n.t("auth.loginFailed") }
    }
  }, [])

  return {
    isAuthenticated,
    login
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, login } = useAuthContext()
  const navigate = useNavigate()

  // 401検知時の共通処理（ログアウト→ログイン画面遷移）を最速で登録し、アンマウント時に解除する
  useLayoutEffect(() => {
    const onUnauthorized = () => handleUnauthorized(navigate)
    setUnauthorizedHandler(onUnauthorized)

    return () => {
      clearUnauthorizedHandler()
    }
  }, [navigate])

  return (
    <UpdateAuthContext.Provider value={{ login }}>
      <AuthContext.Provider value={{ isAuthenticated }}>
        {children}
      </AuthContext.Provider>
    </UpdateAuthContext.Provider>
  )
}
